/**
 * Anti-Ghosting shared metadata: template variables, default sequence,
 * and the body-template variable validator.
 *
 * Lives in `common/` so the recruiter settings UI consumes the same data the
 * server uses — no extra round-trips, no drift between client and server.
 */
import { MessageTransport, SEEDED_TOUCHPOINT_IDS, TouchpointAnchor, } from './clientServerApi';
/** All variables exposed to body templates. */
export const TEMPLATE_VARIABLES = [
    { name: 'firstName', description: "Candidate's first name" },
    { name: 'recruiterName', description: 'Recruiter first name (from logistics)' },
    { name: 'company', description: 'Workspace / company name' },
    { name: 'jobTitle', description: 'Job title (alias: role)' },
    { name: 'role', description: 'Job title (alias of jobTitle)' },
    { name: 'day', description: 'Interview day, e.g. "Tuesday"' },
    { name: 'date', description: 'Interview date, e.g. "May 5"' },
    { name: 'time', description: 'Interview start time in candidate-local TZ' },
    { name: 'address', description: 'Interview address (logistics → falls back to job location)' },
    { name: 'whoToAskFor', description: 'Person to ask for on arrival' },
    { name: 'dressCode', description: 'Dress code instructions' },
    { name: 'parking', description: 'Parking instructions' },
    { name: 'recruiterPhone', description: 'Recruiter callback phone (E.164)' },
];
const VARIABLE_NAMES = new Set(TEMPLATE_VARIABLES.map((v) => v.name));
/** Validate a body template references only known variables. Returns the
 *  offending variable name if any, else null. */
export function findUnknownVariable(template) {
    const re = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g;
    let match;
    while ((match = re.exec(template)) !== null) {
        if (!VARIABLE_NAMES.has(match[1]))
            return match[1];
    }
    return null;
}
/** The seeded default touchpoint sequence. The settings UI starts from this
 *  on first load; "Reset to defaults" reverts to it. */
export const DEFAULT_TOUCHPOINTS = [
    {
        id: SEEDED_TOUCHPOINT_IDS.Confirmation,
        name: 'Confirmation',
        enabled: true,
        anchor: TouchpointAnchor.SlotConfirmed,
        offsetMinutes: 0,
        transport: MessageTransport.Sms,
        bodyTemplate: [
            'Hi {{firstName}}! You\'re confirmed for the {{jobTitle}} interview on {{day}} {{date}} at {{time}}.',
            '{{#if address}}Where: {{address}}{{/if}}',
            '{{#if whoToAskFor}}Ask for: {{whoToAskFor}}{{/if}}',
            '{{#if dressCode}}Dress code: {{dressCode}}{{/if}}',
            '{{#if parking}}Parking: {{parking}}{{/if}}',
            '{{#if recruiterPhone}}Questions? Text or call {{recruiterPhone}}.{{/if}}',
            '— {{recruiterName}}',
        ].join('\n'),
        sortOrder: 10,
    },
    {
        id: SEEDED_TOUCHPOINT_IDS.DayBeforeReminder,
        name: 'Day-before reminder',
        enabled: true,
        anchor: TouchpointAnchor.InterviewStart,
        offsetMinutes: -24 * 60,
        transport: MessageTransport.Sms,
        bodyTemplate: 'Quick reminder, {{firstName}}: your {{jobTitle}} interview is tomorrow ({{day}}) at {{time}}.{{#if address}} {{address}}.{{/if}} See you then! — {{recruiterName}}',
        sortOrder: 20,
    },
    {
        id: SEEDED_TOUCHPOINT_IDS.DayOfReminder,
        name: 'Day-of reminder',
        enabled: true,
        anchor: TouchpointAnchor.InterviewStart,
        offsetMinutes: -3 * 60,
        quietHours: { earliest: '07:00', latest: '09:00' },
        transport: MessageTransport.Sms,
        bodyTemplate: 'Heads up {{firstName}} — today\'s the day. See you at {{time}}{{#if address}} at {{address}}{{/if}}. — {{recruiterName}}',
        sortOrder: 30,
    },
    {
        id: SEEDED_TOUCHPOINT_IDS.FinalReminder,
        name: 'Final reminder',
        enabled: true,
        anchor: TouchpointAnchor.InterviewStart,
        offsetMinutes: -60,
        transport: MessageTransport.Sms,
        bodyTemplate: 'See you in an hour, {{firstName}}!{{#if address}} Address: {{address}}.{{/if}}{{#if whoToAskFor}} Ask for {{whoToAskFor}}.{{/if}} — {{recruiterName}}',
        sortOrder: 40,
    },
    {
        id: SEEDED_TOUCHPOINT_IDS.PostInterviewThanks,
        name: 'Post-interview thanks',
        enabled: true,
        anchor: TouchpointAnchor.InterviewEnd,
        offsetMinutes: 120,
        transport: MessageTransport.Sms,
        bodyTemplate: 'Thanks for coming in today, {{firstName}}! It was great meeting you. We\'ll be in touch with next steps soon. — {{recruiterName}}',
        sortOrder: 50,
    },
    {
        id: SEEDED_TOUCHPOINT_IDS.DecisionOffer,
        name: 'Offer message',
        enabled: true,
        anchor: TouchpointAnchor.RecruiterDecision,
        offsetMinutes: 0,
        transport: MessageTransport.Sms,
        bodyTemplate: 'Great news, {{firstName}} — we\'d like to move forward with you for the {{jobTitle}} role! I\'ll follow up shortly with details. — {{recruiterName}}',
        decisionBranch: 'offer',
        sortOrder: 60,
    },
    {
        id: SEEDED_TOUCHPOINT_IDS.DecisionRejection,
        name: 'Rejection message',
        enabled: true,
        anchor: TouchpointAnchor.RecruiterDecision,
        offsetMinutes: 0,
        transport: MessageTransport.Sms,
        bodyTemplate: 'Hi {{firstName}}, thanks for your time interviewing for the {{jobTitle}} role. We\'ve decided to move forward with another candidate, but truly appreciated meeting you. Wishing you the best. — {{recruiterName}}',
        decisionBranch: 'rejection',
        sortOrder: 70,
    },
];
/** Returns a fresh copy of the default config (touchpoints array is deeply cloned). */
export function buildDefaultAntiGhostingConfig() {
    return {
        enabled: true,
        touchpoints: DEFAULT_TOUCHPOINTS.map((tp) => ({
            ...tp,
            quietHours: tp.quietHours ? { ...tp.quietHours } : undefined,
        })),
    };
}
/**
 * Carrier STOP keywords for candidate-side opt-out.
 *
 * Twilio also enforces these natively at the carrier level — this set is
 * what *we* react to: cancel all Pending follow-up messages for the
 * candidate and persist `Candidate.smsOptedOut`. Intentionally excludes
 * HELP / INFO (those are information requests, not opt-out) and START /
 * UNSTOP / YES (opt-in; v1 doesn't auto-rescind an opt-out).
 */
export const STOP_KEYWORDS = new Set([
    'STOP', 'STOPALL', 'UNSUBSCRIBE', 'CANCEL', 'END', 'QUIT',
]);
/**
 * Returns the matched stop keyword (uppercased) when `text` is exactly one
 * of `STOP_KEYWORDS` (after trim, case-insensitive); otherwise null.
 *
 * "Exact word" semantics: "stop please" does NOT match — only a
 * single-token reply. Mirrors carrier-side behavior so we don't surprise
 * candidates by opting them out for a casual "I'll stop by tomorrow".
 */
export function matchStopKeyword(text) {
    if (!text)
        return null;
    const upper = text.trim().toUpperCase();
    return STOP_KEYWORDS.has(upper) ? upper : null;
}
/** Carrier re-subscribe keywords (Twilio's defaults). They mean "start again" only after a STOP. */
export const START_KEYWORDS = new Set(['START', 'UNSTOP', 'YES']);
/** The matched re-subscribe keyword (uppercased) when `text` is exactly one; otherwise null. */
export function matchStartKeyword(text) {
    const upper = text.trim().toUpperCase();
    return START_KEYWORDS.has(upper) ? upper : null;
}
